/**
 * Remembers, across a full page load, that we handed a customer over to their
 * bank.
 *
 * Starting a sync navigates the browser away, so every bit of in-memory state
 * is gone by the time the customer comes back. Without a note left behind, the
 * only way to tell "mid-sync" from "just arrived" is the status endpoint, and
 * that endpoint legitimately does not know yet: the result reaches us through
 * an asynchronous callback from the aggregator. This is the note.
 */
export class SyncMarker {

    /**
     * @param key          storage key; carries a version suffix so a redeploy
     *                     can never rehydrate an older shape
     * @param ttlMillis    after this an unfinished sync is treated as abandoned,
     *                     so nobody is left on a spinner forever
     * @param storage      injectable for testing; defaults to sessionStorage
     */
    constructor({ key, ttlMillis, storage = undefined }) {
        this.key = key;
        this.ttlMillis = ttlMillis;
        this.injectedStorage = storage;
    }

    /** Null when there is no storage to speak of, e.g. during SSR. */
    get storage() {
        if (this.injectedStorage !== undefined) {
            return this.injectedStorage;
        }
        if (typeof window === "undefined") {
            return null;
        }
        return window.sessionStorage;
    }

    mark({ syncId = null, source = null } = {}) {
        this.write({ syncId, source, startedAt: Date.now() });
    }

    clear() {
        let storage = this.storage;
        if (!storage) {
            return;
        }
        try {
            storage.removeItem(this.key);
        } catch (e) {
            console.log("PSD2:: could not clear the sync marker", e);
        }
    }

    /** The stored marker, or null when absent, unreadable or expired. */
    read() {
        let storage = this.storage;
        if (!storage) {
            return null;
        }
        try {
            let raw = storage.getItem(this.key);
            if (!raw) {
                return null;
            }
            let marker = JSON.parse(raw);
            if (!marker?.startedAt || Date.now() - marker.startedAt > this.ttlMillis) {
                this.clear();
                return null;
            }
            return marker;
        } catch (e) {
            console.log("PSD2:: could not read the sync marker", e);
            return null;
        }
    }

    /**
     * Is a sync running that belongs to *this* use of the component? The same
     * customer can have a correction and an application on the go at once, and
     * one must not put the other on a spinner.
     */
    isActiveFor(source) {
        let marker = this.read();
        return !!marker && marker.source === source;
    }

    write(marker) {
        let storage = this.storage;
        if (!storage) {
            return;
        }
        try {
            storage.setItem(this.key, JSON.stringify(marker));
        } catch (e) {
            console.log("PSD2:: could not store the sync marker", e);
        }
    }
}

export const DEFAULT_SYNC_MARKER_KEY = "instacash_bnpl_psd2_sync_in_flight_v1";
export const DEFAULT_SYNC_MARKER_TTL_MILLIS = 30 * 60 * 1000;

export function createSyncMarker(overrides = {}) {
    return new SyncMarker({
        key: DEFAULT_SYNC_MARKER_KEY,
        ttlMillis: DEFAULT_SYNC_MARKER_TTL_MILLIS,
        ...overrides,
    });
}
